import { StandardEvents } from '@shopify/events';

/**
 * THIRD Space: 商品ページの画像ギャラリーで、各カラー(オプション値)の
 * 代表画像(メイン画像)の上にだけ、そのカラー名のキャプションを重ねて
 * 表示する。同じカラーの他の写真(サブ画像)には表示しない。
 *
 * カラー名はスウォッチのdata-option-value-name(product_option_value.name。
 * 在庫切れの場合aria-labelには「 - 在庫切れ」が付くため、キャプションには
 * この専用属性を使う)、代表画像IDは同じスウォッチのdata-option-media-id
 * から取得する(assets/variant-media-instant-jump.jsと同じデータソース)。
 *
 * data-option-media-idは画像ジャンプ機能のため色以外のオプション(サイズ等)
 * にも付与されている。同じ色の写真が1枚しかない商品では、その色の全サイズの
 * data-option-media-idが同じ画像IDになるため、色以外の値も区別せず拾うと
 * DOM順で後から処理されたサイズ等の値がマップ内で色のラベルを上書きしてしまう
 * (例: 黒の代表画像にサイズ「L」のキャプションが表示される不具合)。
 * snippets/product-card-third-space.liquidと同じ判定で、色オプションの
 * 値だけをキャプション対象にする。
 *
 * サーバーから取得したHTMLでギャラリーが丸ごと入れ替わった場合
 * (assets/variant-picker.jsのfetch完了後)もキャプションを再付与できる
 * よう、初回読み込み時に加えてshopify:product:selectイベント完了後にも
 * 再実行する。ネイティブファイルは変更していない。
 */

const COLOR_OPTION_NAMES = new Set(['color', 'カラー', '色']);

/**
 * @param {Element} scope
 * @returns {Map<string, string>}
 */
function buildCaptionMap(scope) {
  /** @type {Map<string, string>} */
  const map = new Map();

  scope.querySelectorAll('[data-option-media-id]').forEach((input) => {
    const dataset = /** @type {HTMLElement} */ (input).dataset;
    const optionName = dataset.optionName?.trim().toLowerCase();
    if (!optionName || !COLOR_OPTION_NAMES.has(optionName)) return;

    const mediaId = dataset.optionMediaId;
    const label = dataset.optionValueName || input.getAttribute('aria-label');
    if (mediaId && label) map.set(mediaId, label);
  });

  return map;
}

/**
 * @param {Element} scope
 */
function applyCaptions(scope) {
  const mediaGallery = scope.querySelector('media-gallery');
  if (!mediaGallery) return;

  const captionMap = buildCaptionMap(scope);
  if (captionMap.size === 0) return;

  mediaGallery.querySelectorAll('.product-media[data-media-id]').forEach((productMedia) => {
    const mediaId = /** @type {HTMLElement} */ (productMedia).dataset.mediaId;
    const existing = productMedia.querySelector('.ts-media-caption');
    const label = mediaId ? captionMap.get(mediaId) : undefined;

    if (!label) {
      existing?.remove();
      return;
    }

    if (existing) {
      existing.textContent = label;
      return;
    }

    const caption = document.createElement('span');
    caption.className = 'ts-media-caption';
    caption.textContent = label;
    productMedia.appendChild(caption);
  });
}

document.querySelectorAll('.shopify-section, dialog').forEach((scope) => {
  if (scope.querySelector('variant-picker')) applyCaptions(scope);
});

document.addEventListener(StandardEvents.productSelect, (event) => {
  event.promise
    ?.then(() => {
      const scope =
        event.target instanceof Element ? event.target.closest('.shopify-section, dialog') : null;
      if (scope) applyCaptions(scope);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[variant-media-caption] Event promise rejected:', error);
    });
});
